import React, { useEffect, useCallback, useRef, useState, useMemo } from "react";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import {
  closeSymbolPopup,
  setSkipWinAnimation,
  setUserWantsToDismissWinAnimation,
  setAutoplaySpeed,
  closeMenu,
} from "../../../../State/actions";
import {
  selectAnyPopupActive,
  selectSpacebarSpinEnabled,
  selectSessionExpired,
  selectIsSpinning,
  selectAutoPlayActive,
  selectHoldAndWinActive,
  selectLastCommand,
  selectFeatureState,
  selectFlowState,
  selectWindupComplete,
  selectErrorPopupActive,
  selectInfoPopupActive,
} from "../../../../State/selectors";
import store from "../../../../State/Store/store";

import {
  createButtonPressAnimation,
  createPlayButtonRotationAnimation,
  attemptFullscreenToggle,
} from "../../../../utilities";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";

// Play button configuration - matches UI button dimensions
const PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.9,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.25,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.675,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Input state tracking for spacebar and touch interactions
let spacePressed = false;
let spaceStartTime = 0;
let holdTimer = null;
let lastActionTime = 0;
const MIN_ACTION_GAP = 100; // Increased from 60ms to 100ms for better throttling (~10 clicks/sec max)

// Touch state variables mirroring spacebar logic
let touchPressed = false;
let touchStartTime = 0;
let touchHoldTimer = null;
let touchId = null;

// EVENT-LEVEL THROTTLE: Blocks click handler execution at entry point
// Prevents performance degradation from rapid clicking (50+ clicks/sec)
let clickThrottleActive = false;
let clickThrottleTimeout = null;

const ButtonPlay = ({ toolbar }) => {
  const dispatch = useDispatch();
  const [actionInProgress, setActionInProgress] = useState(false);
  const [stopButtonDisabled, setStopButtonDisabled] = useState(false);

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);
  // Track if press animation is currently running (prevents rapid click shrinking)
  const isPressAnimatingRef = useRef(false);
  // Single shared timeout for flash effect (prevents buildup)
  const flashTimeoutRef = useRef(null);
  const { playButtonRef } = useReelRefs();
  const { dimensions, uiSymbolSize, device, orientation, getResponsiveConfig } =
    useDimensions();

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(PLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Refs for latest state access in event handlers
  const setActionInProgressRef = useRef(setActionInProgress);
  const setStopButtonDisabledRef = useRef(setStopButtonDisabled);

  const {
    handleSpin,
    handleManualStop,
    startSpacebarTurbo,
    stopSpacebarTurbo,
    spacebarTurboActive,
  } = useReelControl();


  // Redux state selectors
  const sessionExpired = useSelector(selectSessionExpired);
  const isSpinning = useSelector(selectIsSpinning);
  const windupComplete = useSelector(selectWindupComplete);

  const autoPlayActive = useSelector(selectAutoPlayActive);

  const { winAnimationsPlaying, criticalAnimationPlaying } = useSelector(
    (state) => state.animations
  );
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const lastCommand = useSelector(selectLastCommand);
  const featureState = useSelector(selectFeatureState);
  const flowState = useSelector(selectFlowState); // Legacy fallback

  const anyPopupActive = useSelector(selectAnyPopupActive);
  const spacebarSpinEnabled = useSelector(selectSpacebarSpinEnabled);
  const errorPopupActive = useSelector(selectErrorPopupActive);
  const infoPopupActive = useSelector(selectInfoPopupActive);

  // Refs for latest values
  const spacebarTurboActiveRef = useRef(spacebarTurboActive);
  const stopSpacebarTurboRef = useRef(stopSpacebarTurbo);
  const startSpacebarTurboRef = useRef(startSpacebarTurbo);
  const handleSpinRef = useRef(handleSpin);
  const handleManualStopRef = useRef(handleManualStop);

  const actionInProgressRef = useRef(actionInProgress);
  const stopButtonDisabledRef = useRef(stopButtonDisabled);
  const isSpinningRef = useRef(isSpinning);
  const windupCompleteRef = useRef(windupComplete);
  const attemptFullscreenToggleRef = useRef(attemptFullscreenToggle);

  // Action gating to prevent rapid clicks
  // Controls which action type is currently allowed: 'SPIN', 'STOP', or null (no action)
  const allowedActionRef = useRef(null);

  // Pending stop mechanism - allows user to queue a stop during windup
  // User can click SPIN then immediately STOP, but stop waits for windup to complete
  const pendingStopRef = useRef(false);

  // Update refs when values change
  useEffect(() => {
    spacebarTurboActiveRef.current = spacebarTurboActive;
    stopSpacebarTurboRef.current = stopSpacebarTurbo;
    startSpacebarTurboRef.current = startSpacebarTurbo;
    handleSpinRef.current = handleSpin;
    handleManualStopRef.current = handleManualStop;

    actionInProgressRef.current = actionInProgress;
    stopButtonDisabledRef.current = stopButtonDisabled;
    isSpinningRef.current = isSpinning;
    windupCompleteRef.current = windupComplete;
    setActionInProgressRef.current = setActionInProgress;
    setStopButtonDisabledRef.current = setStopButtonDisabled;
  }, [
    spacebarTurboActive,
    stopSpacebarTurbo,
    startSpacebarTurbo,
    handleSpin,
    handleManualStop,

    actionInProgress,
    stopButtonDisabled,
    setActionInProgress,
    setStopButtonDisabled,
    isSpinning,
    windupComplete,
  ]);

  // Update function refs separately (these are imported functions, not state)
  useEffect(() => {
    attemptFullscreenToggleRef.current = attemptFullscreenToggle;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // attemptFullscreenToggle is a stable import

  // Button state

  // Initialize action gate on mount
  useEffect(() => {
    // On mount, if not spinning, allow spin
    if (!isSpinning) {
      allowedActionRef.current = 'SPIN';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount - isSpinning checked but not in deps intentionally

  // Action gate management - controls which action is allowed at each phase
  // Prevents rapid clicks by blocking actions that don't match the current game phase
  useEffect(() => {
    // When windup completes and we're spinning, allow STOP action
    if (isSpinning && windupComplete) {
      allowedActionRef.current = 'STOP';

      // Check if there's a pending stop queued during windup and execute it
      if (pendingStopRef.current) {
        pendingStopRef.current = false;
        allowedActionRef.current = null; // Lock immediately after executing
        handleManualStop(); // Execute the queued stop
      }
    }
  }, [isSpinning, windupComplete, handleManualStop]);

  // When spin completes, allow SPIN action for next spin (ONLY in manual mode, NOT autoplay)
  useEffect(() => {
    if (!isSpinning) {
      if (!autoPlayActive) {
        // Manual mode: Allow SPIN for next spin
        allowedActionRef.current = 'SPIN';
        pendingStopRef.current = false; // Clear any pending stop
      } else {
        // Autoplay mode: Keep gate NULL, spins are triggered automatically by ReelControlContext
        allowedActionRef.current = null;
        pendingStopRef.current = false; // Clear any pending stop
      }
    }
  }, [isSpinning, autoPlayActive]);

  // Manage button enabled/disabled state based on game phase
  // Button must stay disabled during windup for ALL spins (manual + autoplay)
  // This prevents clicking before reels are actually spinning, which causes infinite spin bug
  useEffect(() => {
    // When spin completes, reset actionInProgress and re-enable button for next action
    if (!isSpinning && actionInProgress) {
      setActionInProgress(false);
      setStopButtonDisabled(false);
      return;
    }

    // Wait for windup to complete before allowing stop
    // Button re-enables when: spin is active AND windup is complete
    // If actionInProgress is true, it means we already queued/executed a stop, so keep it disabled
    if (isSpinning && windupComplete && !actionInProgress) {
      setActionInProgress(false);
      setStopButtonDisabled(false);
      return;
    }
  }, [actionInProgress, isSpinning, autoPlayActive, windupComplete]);

  // ONLY reset button state and stop turbo for BIG WIN animations
  // Counter animations and expanding wilds should NOT reset button state
  // This allows turbo to resume after counter finishes if button still held
  useEffect(() => {
    if (winAnimationsPlaying) {
      // Clean up touch state completely for big win animations
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // Clean up spacebar state completely for big win animations
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      spacePressed = false;
      spaceStartTime = 0;

      // Stop turbo completely for big win animations
      if (spacebarTurboActive) {
        const currentState = store.getState();
        if (currentState.settings.autoPlaySpeed === "turbo") {
          dispatch(setAutoplaySpeed("auto"));
        }
        stopSpacebarTurboRef.current();
      }
    }
  }, [winAnimationsPlaying, spacebarTurboActive, dispatch]);

  // CRITICAL: Clear hold state when critical animation starts (Hold and Win detection ONLY)
  // This prevents button release from triggering a spin when Hold and Win is detected
  // User might be holding button when Hold and Win triggers, so we clear hold state immediately
  // NOTE: Expanding wilds also set criticalAnimationPlaying, but we DON'T clear hold state for those
  // (they should work like regular wins - turbo continues after counter finishes)
  useEffect(() => {
    if (criticalAnimationPlaying) {
      // Check if this is for Hold and Win (not expanding wilds)
      // Use universal featureState if available, fall back to legacy flowState
      const isHoldAndWinTrigger =
        FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
        ((featureState && featureState.type === "holdAndWin" && featureState.phase === "intro") ||
         (lastCommand === "spin" && flowState === "respin"));

      // Only clear hold state for Hold and Win, NOT for expanding wilds
      if (isHoldAndWinTrigger || holdAndWinActive) {
        // Clean up touch state completely when Hold and Win starts
        if (touchHoldTimer) {
          clearTimeout(touchHoldTimer);
          touchHoldTimer = null;
        }
        touchPressed = false;
        touchStartTime = 0;
        touchId = null;

        // Clean up spacebar state completely when Hold and Win starts
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }
        spacePressed = false;
        spaceStartTime = 0;

        // Stop turbo if active when Hold and Win starts
        if (spacebarTurboActive) {
          const currentState = store.getState();
          if (currentState.settings.autoPlaySpeed === "turbo") {
            dispatch(setAutoplaySpeed("auto"));
          }
          stopSpacebarTurboRef.current();
        }
      }
      // If it's expanding wilds (criticalAnimationPlaying but NOT Hold and Win),
      // do nothing - let turbo continue naturally after counter finishes
    }
  }, [
    criticalAnimationPlaying,
    spacebarTurboActive,
    dispatch,
    holdAndWinActive,
    featureState,
    lastCommand,
    flowState,
  ]);

  // Safety Effect: Stop turbo and clear hold state when error/info popups appear
  // Prevents turbo spins from continuing when critical popups are displayed (e.g., free rounds ending)
  // Similar to how turbo stops for win animations, this ensures users can't bypass important messages
  const previousPopupFlagsRef = useRef({
    errorPopupActive: false,
    infoPopupActive: false,
  });

  useEffect(() => {
    const errorPopupJustOpened =
      errorPopupActive && !previousPopupFlagsRef.current.errorPopupActive;
    const infoPopupJustOpened =
      infoPopupActive && !previousPopupFlagsRef.current.infoPopupActive;

    // Stop turbo and clear hold state if any popup just opened
    if ((errorPopupJustOpened || infoPopupJustOpened) && spacebarTurboActive) {
      // Clean up touch state completely for popups
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // Clean up spacebar state completely for popups
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      spacePressed = false;
      spaceStartTime = 0;

      // Stop turbo completely for popups
      if (spacebarTurboActive) {
        const currentState = store.getState();
        if (currentState.settings.autoPlaySpeed === "turbo") {
          dispatch(setAutoplaySpeed("auto"));
        }
        stopSpacebarTurboRef.current();
      }
    }

    previousPopupFlagsRef.current = {
      errorPopupActive,
      infoPopupActive,
    };
  }, [errorPopupActive, infoPopupActive, spacebarTurboActive, dispatch]);

  // ============================================================================
  // CENTRALIZED ACTION RESOLUTION SYSTEM
  // ============================================================================

  // Determine if turbo mode should be allowed to START (no animations active)
  // Note: This is for STARTING turbo, not for continuing it after counter animations
  const shouldAllowTurbo = useCallback(() => {
    const state = store.getState();
    const counterIsShowing =
      state.animations.counterAnimationStarted &&
      !state.animations.counterAnimationFinished;

    return !(state.animations.winAnimationsPlaying || counterIsShowing);
  }, []);

  // Resolve what action the button should perform
  const resolveButtonAction = useCallback(() => {
    const state = store.getState();
    const autoPlayActive = state.settings.autoPlayActive;
    const autoPlaySpeed = state.settings.autoPlaySpeed;

    // Priority 1: Skip animations if they're active
    if (state.animations.winAnimationsPlaying) {
      return {
        type: "SKIP_ANIMATIONS",
        winAnimationsPlaying: state.animations.winAnimationsPlaying,
        isWaitingForConfirmation: state.animations.isWaitingForUserConfirmation,
      };
    }

    // Priority 2: Block ALL actions if autoplay is active AND speed is turbo
    if (autoPlayActive && autoPlaySpeed === "turbo") {
      return {
        type: "NO_OP",
      };
    }

    // Priority 3: Stop spin if spinning
    // CRITICAL: During autoplay, if manual stop is already pending, don't allow another stop
    if (state.spin.isSpinning) {
      if (autoPlayActive && state.spin.manualStop) {
        // Manual stop already triggered, don't allow another one
        return {
          type: "NO_OP",
        };
      }
      return {
        type: "STOP_SPIN",
        autoPlayActive: autoPlayActive,
        autoPlaySpeed: autoPlaySpeed,
      };
    }

    // Priority 4: Block starting new spin if autoplay is active
    if (autoPlayActive) {
      return {
        type: "NO_OP",
      };
    }

    // Priority 5: Start spin
    return {
      type: "START_SPIN",
    };
  }, []);

  // Execute skip animations action
  const executeSkipAnimations = useCallback(
    (actionData) => {
      soundManager.play(SOUND_NAME.button_click);

      if (actionData.winAnimationsPlaying) {
        if (actionData.isWaitingForConfirmation) {
          dispatch(setUserWantsToDismissWinAnimation(true));
        } else {
          dispatch(setSkipWinAnimation(true));
        }
      }
    },
    [dispatch]
  );

  // Execute stop spin action
  const executeStopSpin = useCallback((actionData) => {
    // Regular reel stop - check turbo restrictions
    // Don't allow manual stop in regular turbo mode (non-autoplay)
    if (!actionData.autoPlayActive && actionData.autoPlaySpeed === "turbo") {
      return false;
    }

    // Don't allow manual stop in autoplay turbo mode (already blocked in resolveButtonAction, but double-check)
    if (actionData.autoPlayActive && actionData.autoPlaySpeed === "turbo") {
      return false;
    }

    // Block ALL actions until spin completes
    allowedActionRef.current = null;

    soundManager.play(SOUND_NAME.button_click);
    lastActionTime = Date.now();
    setActionInProgressRef.current(true);
    setStopButtonDisabledRef.current(true);
    handleManualStopRef.current();

    return true;
  }, []);

  // Execute start spin action
  const executeStartSpin = useCallback(() => {
    const state = store.getState();

    // Block manual spin if autoplay is active
    // Autoplay manages its own spin cycle - manual spins should not interfere
    if (state.settings.autoPlayActive) {
      return false;
    }

    // Block ALL actions during windup phase
    allowedActionRef.current = null;

    // Trigger rotation animation on the play button (secondary theme signature animation)
    if (playButtonRef.current) {
      createPlayButtonRotationAnimation(playButtonRef.current);
    }

    // Allow spin regardless of balance - API will handle insufficient credits
    soundManager.play(SOUND_NAME.spin);
    lastActionTime = Date.now();
    actionInProgressRef.current = true;
    handleSpinRef.current();

    return true;
  }, [playButtonRef]);

  // Flash brightness effect on click/tap (visual feedback)
  // Uses single shared timeout to prevent buildup at high click rates
  const flashBrightness = useCallback(() => {
    if (!playButtonRef.current) return;

    // Clear any existing timeout (prevents buildup - only 1 timeout max)
    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }

    // Apply brightness filter
    playButtonRef.current.filters = [brightnessFilter.current];

    // Single timeout that gets reset on each click (no buildup)
    flashTimeoutRef.current = setTimeout(() => {
      if (playButtonRef.current && !isHoveredRef.current) {
        playButtonRef.current.filters = [];
      }
      flashTimeoutRef.current = null;
    }, 120);
  }, [playButtonRef]);

  // Master action executor
  const executeButtonAction = useCallback(
    (action, source = "click") => {
      // Visual feedback already handled at top of handleClick and handleKeyDown
      // No need to add it here anymore

      switch (action.type) {
        case "SKIP_ANIMATIONS":
          executeSkipAnimations(action);
          break;
        case "STOP_SPIN":
          executeStopSpin(action);
          break;
        case "START_SPIN":
          executeStartSpin();
          break;
        case "NO_OP":
          // Do nothing - autoplay is active, just ignore the click
          break;
        default:
          // Unknown action type - ignore
          break;
      }
    },
    [executeSkipAnimations, executeStopSpin, executeStartSpin]
  );

  // ============================================================================
  // SIMPLIFIED EVENT HANDLERS
  // ============================================================================

  // Handle click function - now uses centralized action resolution
  const handleClick = useCallback(() => {
    // EVENT-LEVEL THROTTLE: FIRST LINE - Instant return if throttle is active
    // Prevents performance degradation from rapid clicking (50+ clicks/sec)
    if (clickThrottleActive) {
      return; // Instant exit - zero overhead, no logs, no code execution
    }

    // Set throttle flag IMMEDIATELY and schedule single reset
    clickThrottleActive = true;
    if (clickThrottleTimeout) {
      clearTimeout(clickThrottleTimeout);
    }
    clickThrottleTimeout = setTimeout(() => {
      clickThrottleActive = false;
      clickThrottleTimeout = null;
    }, MIN_ACTION_GAP);

    // EARLY TURBO CHECK: Skip all expensive work if turbo mode is active
    // This check happens before state reads, action resolution, and animations
    const quickState = store.getState();
    if (quickState.settings.autoPlayActive && quickState.settings.autoPlaySpeed === "turbo") {
      return; // Exit immediately - no visual feedback, no expensive work
    }

    // Determine what action will be taken
    const action = resolveButtonAction();

    // Block visual feedback during rotation animation (windup phase)
    const isRotating = quickState.spin.isSpinning && !quickState.spin.windupComplete;

    // ALWAYS provide visual feedback first (before any checks) - except during rotation or START_SPIN
    // START_SPIN has its own rotation animation, so we don't want press animation
    if (!isRotating && action.type !== 'START_SPIN') {
      // Only start new animation if one isn't already running (debouncing)
      if (playButtonRef.current && !isPressAnimatingRef.current) {
        isPressAnimatingRef.current = true;
        createButtonPressAnimation(playButtonRef.current, {
          targetWidth: buttonConfig.width,
          targetHeight: buttonConfig.height,
          onComplete: () => {
            isPressAnimatingRef.current = false;
          }
        });
      }
      flashBrightness();
    }

    // Close menu if open and attempt fullscreen toggle
    dispatch(closeMenu());
    attemptFullscreenToggleRef.current(dispatch, device);

    // Block clicks if there's a win that hasn't been fully displayed yet
    // Only applies when counterAnimationStarted is true — animated wins (Big Win, H&W trigger)
    // skip the counter so they must not be blocked by this check.
    const hasWin =
      quickState.game.currentRound?.win && quickState.game.currentRound.win > 0;
    const winNotFullyDisplayed =
      hasWin &&
      quickState.spin.spinComplete &&
      quickState.animations.counterAnimationStarted &&
      !quickState.animations.counterAnimationFinished;

    // Check for critical animation (expanding wilds, etc.)
    if (quickState.animations.criticalAnimationPlaying) {
      return; // Visual feedback already played
    }

    if (
      anyPopupActive ||
      sessionExpired ||
      quickState.userInterface.userInterfaceDisabled ||
      actionInProgressRef.current ||
      winNotFullyDisplayed
    ) {
      return; // Visual feedback already played
    }

    const now = Date.now();
    if (now - lastActionTime < MIN_ACTION_GAP) {
      return; // Visual feedback already played
    }

    // Action gating - block if action type doesn't match what's allowed
    if (action.type === 'START_SPIN' && allowedActionRef.current !== 'SPIN') {
      return;
    }
    if (action.type === 'STOP_SPIN') {
      if (allowedActionRef.current === 'STOP') {
        // Windup complete, execute stop immediately
      } else if (allowedActionRef.current === null && isSpinningRef.current && !windupCompleteRef.current) {
        // During windup, queue the stop (only once)
        if (!pendingStopRef.current) {
          pendingStopRef.current = true;

          // Disable button immediately when stop is queued
          setActionInProgress(true);
        }
        return; // Don't execute yet (visual feedback already played at top)
      } else {
        // Not allowed at all
        return;
      }
    }

    executeButtonAction(action, "click");
  }, [
    anyPopupActive,
    sessionExpired,
    resolveButtonAction,
    executeButtonAction,
    playButtonRef,
    flashBrightness,
    dispatch,
    device,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Touch pointer down handler - handles turbo activation on hold
  const handlePointerDown = useCallback(
    (event) => {
      const state = store.getState();

      // Block touch while counter is showing (PopiWinDisplay displays for 2 seconds)
      const counterIsShowing =
        state.animations.counterAnimationStarted &&
        !state.animations.counterAnimationFinished;

      // Check for critical animation (expanding wilds, etc.)
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      if (
        touchPressed ||
        selectAnyPopupActive(state) ||
        state.game.sessionExpired ||
        state.userInterface.userInterfaceDisabled ||
        actionInProgressRef.current ||
        counterIsShowing
      ) {
        return;
      }

      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) return;

      event.stopPropagation();
      event.preventDefault();

      // Block ALL actions if autoplay is active AND speed is turbo
      if (
        state.settings.autoPlayActive &&
        state.settings.autoPlaySpeed === "turbo"
      ) {
        return;
      }

      // Handle autoplay: Simple one-action rule
      // If spinning, trigger manual stop ONCE, then clear hold state
      if (state.settings.autoPlayActive) {
        // If already pressed, ignore (prevents duplicate stops)
        if (touchPressed) {
          return;
        }

        // Mark as pressed to prevent duplicates
        touchPressed = true;
        touchStartTime = now;
        touchId = event.pointerId;

        // If spinning, trigger manual stop once
        if (state.spin.isSpinning && !state.spin.manualStop) {
          const action = resolveButtonAction();

          // Action gating for touch with pending stop support
          if (action.type === "STOP_SPIN") {
            if (allowedActionRef.current === 'STOP') {
              // Windup complete, execute stop immediately
              executeButtonAction(action, "click");
            } else if (allowedActionRef.current === null && state.spin.isSpinning && !state.spin.windupComplete) {
              // During windup, queue the stop (only once)
              if (!pendingStopRef.current) {
                pendingStopRef.current = true;

                // Add visual feedback when queuing stop
                if (playButtonRef.current && !isPressAnimatingRef.current) {
                  isPressAnimatingRef.current = true;
                  createButtonPressAnimation(playButtonRef.current, {
                    targetWidth: buttonConfig.width,
                    targetHeight: buttonConfig.height,
                    onComplete: () => {
                      isPressAnimatingRef.current = false;
                    }
                  });
                }

                // Disable button immediately when stop is queued
                setActionInProgressRef.current(true);
              }
            }
          }
        }

        // Clear hold timer (no turbo during autoplay)
        if (touchHoldTimer) {
          clearTimeout(touchHoldTimer);
          touchHoldTimer = null;
        }

        // Don't set up turbo timer - autoplay doesn't allow turbo
        return;
      }

      // Stop turbo if already active (from spacebar)
      if (spacebarTurboActiveRef.current) {
        stopSpacebarTurboRef.current();
        return;
      }

      // Setup touch tracking (needed for both tap and hold detection)
      touchId = event.pointerId;
      touchPressed = true;
      touchStartTime = Date.now();

      // Only start turbo timer if animations are NOT active
      if (shouldAllowTurbo()) {
        // Start turbo after hold
        touchHoldTimer = setTimeout(() => {
          if (touchPressed && !spacebarTurboActiveRef.current) {
            // Double-check animations haven't started
            if (!shouldAllowTurbo()) {
              return;
            }

            const currentState = store.getState();

            // CRITICAL: Block turbo activation during autoplay
            if (currentState.settings.autoPlayActive) {
              touchPressed = false;
              touchStartTime = 0;
              touchId = null;
              return;
            }

            if (
              !currentState.spin.isSpinning &&
              !currentState.settings.autoPlayActive
            ) {
              // Allow turbo regardless of balance - API will handle insufficient credits
              lastActionTime = Date.now();
              startSpacebarTurboRef.current();

            }
          }
        }, FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY);
      }
    },
    [resolveButtonAction, executeButtonAction, shouldAllowTurbo, playButtonRef, buttonConfig.width, buttonConfig.height]
  );

  // Touch pointer up handler - stops turbo or performs quick tap action
  const handlePointerUp = useCallback(
    (event) => {
      if (!touchPressed || event.pointerId !== touchId) return;

      event.stopPropagation();
      event.preventDefault();

      const state = store.getState();

      // Handle autoplay: On release, just reset state - NO ACTIONS
      // Manual stop should ONLY happen on pointerDown, not on pointerUp
      // This prevents double-stop issues that cause infinite spin bugs
      if (state.settings.autoPlayActive) {
        // Clear timer and reset state
        if (touchHoldTimer) {
          clearTimeout(touchHoldTimer);
          touchHoldTimer = null;
        }
        touchPressed = false;
        touchStartTime = 0;
        touchId = null;

        return; // No turbo during autoplay, no actions on release
      }

      // Block clicks if there's a win that hasn't been fully displayed yet
      // Only applies when counterAnimationStarted is true — animated wins skip the counter.
      const hasWin =
        state.game.currentRound?.win && state.game.currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin &&
        state.spin.spinComplete &&
        state.animations.counterAnimationStarted &&
        !state.animations.counterAnimationFinished;

      const holdDuration = Date.now() - touchStartTime;

      // Clear timer and reset state immediately to prevent stuck state
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      if (actionInProgressRef.current) return;

      // If critical animation is playing AND turbo is active, stop turbo (button released during critical animation)
      if (
        state.animations.criticalAnimationPlaying &&
        spacebarTurboActiveRef.current
      ) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if critical animation is playing
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      // If win is being displayed AND turbo is active, stop turbo (button released during win display)
      if (winNotFullyDisplayed && spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if win is being displayed
      if (winNotFullyDisplayed) {
        return;
      }

      // Stop turbo if active
      if (spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
      } else if (holdDuration < FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY) {
        // Quick tap - resolve and execute action
        const action = resolveButtonAction();

        // Action gating for touch
        if (action.type === 'START_SPIN' && allowedActionRef.current !== 'SPIN') {
          return;
        }
        if (action.type === 'STOP_SPIN' && allowedActionRef.current !== 'STOP') {
          return;
        }

        executeButtonAction(action, "click");
      }
    },
    [resolveButtonAction, executeButtonAction]
  );

  // Touch pointer cancel handler - cleanup on touch interruption
  const handlePointerCancel = useCallback((event) => {
    if (event.pointerId !== touchId) return;

    event.stopPropagation();
    event.preventDefault();

    // Clear timer and stop turbo if active
    if (touchHoldTimer) {
      clearTimeout(touchHoldTimer);
      touchHoldTimer = null;
    }
    if (spacebarTurboActiveRef.current) {
      stopSpacebarTurboRef.current();
    }

    // Reset touch state
    touchPressed = false;
    touchStartTime = 0;
    touchId = null;
  }, []);

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar width instead of full screen width
  const buttonPosition = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -dimensions.height / 5.0,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width / 1.65,
          y: -dimensions.height / 2 + buttonConfig.height / 2.0,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -dimensions.height / 5.0,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width / 1.4,
          y: toolbar.height - buttonConfig.height / 1.7,
        };
      }
    } else {
      const yOffset = 0.04;
      return {
        x: toolbar.width - buttonConfig.width * 1.275,
        y: uiSymbolSize * yOffset,
      };
    }
  }, [
    device,
    orientation,
    dimensions.height,
    toolbar.width,
    toolbar.height,
    buttonConfig.width,
    buttonConfig.height,
    uiSymbolSize,
  ]);

  // Get the appropriate icon texture - always use play icon
  const iconTexture = PIXI.Assets.get(ASSET_NAME.icon_play);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    // Always allow hover effect for better UX
    if (playButtonRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !playButtonRef.current.filters ||
        playButtonRef.current.filters.length === 0
      ) {
        playButtonRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [playButtonRef, brightnessFilter]);

  const handlePointerOut = useCallback(() => {
    if (playButtonRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      playButtonRef.current.filters = [];
    }

    // CRITICAL: Trigger liftoff if pointer leaves while button is held
    // This handles the edge case where user holds button then drags off it
    if (touchPressed) {
      // Clear timer and reset state immediately to prevent stuck state
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }

      // Reset touch state
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // FORCEFULLY stop turbo if active (user dragged finger off button while turbo was running)
      if (spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
      }
    }
  }, [playButtonRef]);


  // Prevent context menu on long press/touch-and-hold
  useEffect(() => {
    const preventContextMenu = (event) => {
      event.preventDefault();
      return false;
    };

    document.addEventListener("contextmenu", preventContextMenu, {
      passive: false,
    });
    return () => {
      document.removeEventListener("contextmenu", preventContextMenu);

      // Clear flash timeout to prevent memory leaks
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = null;
      }
    };
  }, []);

  // Keyboard event handling - spacebar turbo activation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Block spacebar if disabled in settings
      if (!spacebarSpinEnabled) {
        return;
      }

      event.preventDefault();

      // EVENT-LEVEL THROTTLE: Instant return if throttle is active
      // Prevents performance degradation from rapid spacebar spam
      if (clickThrottleActive) {
        return; // Instant exit - zero overhead
      }

      // Set throttle flag IMMEDIATELY and schedule single reset
      clickThrottleActive = true;
      if (clickThrottleTimeout) {
        clearTimeout(clickThrottleTimeout);
      }
      clickThrottleTimeout = setTimeout(() => {
        clickThrottleActive = false;
        clickThrottleTimeout = null;
      }, MIN_ACTION_GAP);

      const state = store.getState();

      // Close menu if open (matches click behavior)
      dispatch(closeMenu());

      if (state.userInterface.symbolPopupActive) {
        dispatch(closeSymbolPopup());
      }

      const updatedState = store.getState();

      // Determine what action will be taken
      const action = resolveButtonAction();

      // ALWAYS provide visual feedback first (before any checks) - except during rotation or START_SPIN
      // START_SPIN has its own rotation animation, so we don't want press animation
      const isRotating = updatedState.spin.isSpinning && !updatedState.spin.windupComplete;
      if (!isRotating && action.type !== 'START_SPIN' && playButtonRef.current && !isPressAnimatingRef.current) {
        isPressAnimatingRef.current = true;
        createButtonPressAnimation(playButtonRef.current, {
          targetWidth: buttonConfig.width,
          targetHeight: buttonConfig.height,
          onComplete: () => {
            isPressAnimatingRef.current = false;
          }
        });
      }

      // Block spacebar while counter is showing (PopiWinDisplay displays for 2 seconds)
      const counterIsShowing =
        updatedState.animations.counterAnimationStarted &&
        !updatedState.animations.counterAnimationFinished;

      // Check for critical animation (expanding wilds, etc.)
      if (updatedState.animations.criticalAnimationPlaying) {
        return;
      }

      if (
        selectAnyPopupActive(updatedState) ||
        updatedState.game.sessionExpired ||
        updatedState.userInterface.userInterfaceDisabled ||
        actionInProgressRef.current ||
        counterIsShowing
      ) {
        return;
      }

      // Ignore key repeat events (if spacebar is already pressed, ignore)
      if (spacePressed && event.repeat) {
        return;
      }

      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) return;

      // Block ALL actions if autoplay is active AND speed is turbo
      if (
        updatedState.settings.autoPlayActive &&
        updatedState.settings.autoPlaySpeed === "turbo"
      ) {
        return;
      }

      // Handle autoplay: Simple one-action rule
      // If spinning, trigger manual stop ONCE, then clear hold state
      if (updatedState.settings.autoPlayActive) {
        // If already pressed, ignore (prevents duplicate stops from key repeat)
        if (spacePressed) {
          return;
        }

        // Mark as pressed to prevent duplicates
        spacePressed = true;
        spaceStartTime = now;

        // If spinning, trigger manual stop once
        if (updatedState.spin.isSpinning && !updatedState.spin.manualStop) {
          // Action gating for spacebar with pending stop support
          if (action.type === "STOP_SPIN") {
            if (allowedActionRef.current === 'STOP') {
              // Windup complete, execute stop immediately
              executeButtonAction(action, "spacebar");
            } else if (allowedActionRef.current === null && updatedState.spin.isSpinning && !updatedState.spin.windupComplete) {
              // During windup, queue the stop (only once)
              if (!pendingStopRef.current) {
                pendingStopRef.current = true;

                // Disable button immediately when stop is queued
                setActionInProgressRef.current(true);
              }
            }
          }
        }

        // Clear hold timer (no turbo during autoplay)
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }

        // Don't set up turbo timer - autoplay doesn't allow turbo
        return;
      }

      // Start turbo after hold (but not during animations)
      if (!spacePressed) {
        // Setup spacebar tracking (needed for both tap and hold detection)
        spacePressed = true;
        spaceStartTime = now;

        // Only start turbo timer if animations are NOT active
        if (shouldAllowTurbo()) {
          holdTimer = setTimeout(() => {
            if (spacePressed) {
              // Double-check animations haven't started
              if (!shouldAllowTurbo()) {
                return;
              }

              const currentState = store.getState();

              // CRITICAL: Block turbo activation during autoplay
              if (currentState.settings.autoPlayActive) {
                spacePressed = false;
                spaceStartTime = 0;
                return;
              }

              if (
                !currentState.spin.isSpinning &&
                !currentState.settings.autoPlayActive
              ) {
                // Allow turbo regardless of balance - API will handle insufficient credits
                lastActionTime = Date.now();
                startSpacebarTurboRef.current();

              }
            }
          }, FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY);
        }
      }
    };

    const handleKeyUp = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Block spacebar if disabled in settings
      if (!spacebarSpinEnabled) {
        return;
      }

      if (!spacePressed) return;

      const state = store.getState();

      // Handle autoplay: On release, just reset state - NO ACTIONS
      // Manual stop should ONLY happen on keyDown, not on keyUp
      // This prevents double-stop issues that cause infinite spin bugs
      if (state.settings.autoPlayActive) {
        // Clear timer and reset state
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }
        spacePressed = false;
        spaceStartTime = 0;

        return; // No turbo during autoplay, no actions on release
      }

      // Block clicks if there's a win that hasn't been fully displayed yet
      // Only applies when counterAnimationStarted is true — animated wins skip the counter.
      const hasWin =
        state.game.currentRound?.win && state.game.currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin &&
        state.spin.spinComplete &&
        state.animations.counterAnimationStarted &&
        !state.animations.counterAnimationFinished;

      const holdDuration = Date.now() - spaceStartTime;

      // Clear timer and reset state immediately to prevent stuck state
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      spacePressed = false;
      spaceStartTime = 0;

      if (actionInProgressRef.current) {
        return;
      }

      // If critical animation is playing AND turbo is active, stop turbo (spacebar released during critical animation)
      if (
        state.animations.criticalAnimationPlaying &&
        spacebarTurboActiveRef.current
      ) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if critical animation is playing
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      // If win is being displayed AND turbo is active, stop turbo (button released during win display)
      if (winNotFullyDisplayed && spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if win is being displayed
      if (winNotFullyDisplayed) {
        return;
      }

      // Stop turbo if active, otherwise perform quick tap action
      if (spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
      } else if (holdDuration < FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY) {
        // Quick tap - resolve and execute action
        const action = resolveButtonAction();

        // Action gating for spacebar with pending stop support
        if (action.type === 'START_SPIN' && allowedActionRef.current !== 'SPIN') {
          return;
        }
        if (action.type === 'STOP_SPIN') {
          if (allowedActionRef.current === 'STOP') {
            // Windup complete, execute stop immediately
          } else if (allowedActionRef.current === null && isSpinningRef.current && !windupCompleteRef.current) {
            // During windup, queue the stop (only once)
            if (!pendingStopRef.current) {
              pendingStopRef.current = true;

              // Add visual feedback when queuing stop
              if (playButtonRef.current && !isPressAnimatingRef.current) {
                isPressAnimatingRef.current = true;
                createButtonPressAnimation(playButtonRef.current, {
                  targetWidth: buttonConfig.width,
                  targetHeight: buttonConfig.height,
                  onComplete: () => {
                    isPressAnimatingRef.current = false;
                  }
                });
              }

              // Disable button immediately when stop is queued
              setActionInProgressRef.current(true);
            }
            return; // Don't execute yet
          } else {
            // Not allowed at all
            return;
          }
        }

        executeButtonAction(action, "spacebar");
      }
    };

    const handleWindowBlur = () => {
      // Cleanup on window blur
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      if (spacebarTurboActiveRef.current) {
        stopSpacebarTurboRef.current();
      }
      spacePressed = false;
      spaceStartTime = 0;
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      // Cleanup: clear all timers, stop turbo, reset state
      if (holdTimer) clearTimeout(holdTimer);
      if (touchHoldTimer) clearTimeout(touchHoldTimer);
      if (spacebarTurboActiveRef.current) {
        stopSpacebarTurboRef.current();
      }

      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);

      spacePressed = false;
      spaceStartTime = 0;

      // Cleanup throttle timeout
      if (clickThrottleTimeout) {
        clearTimeout(clickThrottleTimeout);
        clickThrottleTimeout = null;
        clickThrottleActive = false;
      }
      holdTimer = null;
      touchPressed = false;
      touchStartTime = 0;
      touchHoldTimer = null;
      touchId = null;
    };
  }, [
    dispatch,
    resolveButtonAction,
    executeButtonAction,
    shouldAllowTurbo,
    playButtonRef,
    spacebarSpinEnabled,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(() => buttonConfig.width / 2, [buttonConfig.width]);

  return (
    <Container name="secondary_button_play" x={buttonPosition.x} y={buttonPosition.y}>
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_play_interactive_area"
        eventMode="static"
        cursor="pointer"
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        onpointerdown={handlePointerDown}
        onpointerup={handlePointerUp}
        onpointercancel={handlePointerCancel}
        onpointertap={handleClick}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={playButtonRef}
        texture={iconTexture}
        name="button_play_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        alpha={1}
        resolution={2}
      />
    </Container>
  );
};

export default ButtonPlay;
